import { logs, SeverityNumber } from '@opentelemetry/api-logs'

const otelLogger = logs.getLogger('web')

const SEVERITY: Record<'INFO' | 'DEBUG' | 'ERROR' | 'WARN', SeverityNumber> = {
  DEBUG: SeverityNumber.DEBUG,
  INFO: SeverityNumber.INFO,
  WARN: SeverityNumber.WARN,
  ERROR: SeverityNumber.ERROR,
}

const LOG_LEVEL = process.env.LOG_LEVEL || 'DEBUG'

const shouldLog = (level: 'INFO' | 'DEBUG' | 'ERROR' | 'WARN') => {
  if (typeof window !== 'undefined') return true

  switch (level) {
    case 'DEBUG':
      return LOG_LEVEL === 'DEBUG'
    case 'INFO':
      return LOG_LEVEL === 'DEBUG' || LOG_LEVEL === 'INFO'
    case 'WARN':
      return LOG_LEVEL === 'DEBUG' || LOG_LEVEL === 'INFO' || LOG_LEVEL === 'WARN'
    case 'ERROR':
    default:
      return true
  }
}

const formatPart = (part: unknown): string => {
  if (typeof part === 'string') return part
  if (part instanceof Error) return part.stack ?? part.message
  try {
    return JSON.stringify(part)
  } catch {
    return String(part)
  }
}

type LogOptions = string | Partial<{ group: string }>

const log = (
  options: LogOptions,
  message: unknown[],
  type: 'INFO' | 'DEBUG' | 'ERROR' | 'WARN',
) => {
  if (!shouldLog(type)) return

  const group = typeof options === 'string' ? options : options.group
  const prefix = `[${type}]${group ? `[${group}]` : ''}`

  console.log(prefix, ...message)

  // Prevent DEBUG logs from being sent to OpenTelemetry, as they are too verbose and not useful for tracing.
  if (type === 'DEBUG') return

  otelLogger.emit({
    severityNumber: SEVERITY[type],
    severityText: type,
    body: [prefix, ...message.map(formatPart)].join(' '),
  })
}

export const logger = {
  error: (options: LogOptions, ...message: unknown[]) => log(options, message, 'ERROR'),
  warn: (options: LogOptions, ...message: unknown[]) => log(options, message, 'WARN'),
  info: (options: LogOptions, ...message: unknown[]) => log(options, message, 'INFO'),
  debug: (options: LogOptions, ...message: unknown[]) => log(options, message, 'DEBUG'),
}
